import React, { useState, useEffect } from 'react';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import Sprint from '../../../interfaces/sprint/Sprint';
import Ticket from '../../../interfaces/ticket/Ticket';
import SprintLane from './SprintLane';
import './IssuesPage.css';
import { ReorderRequest } from '../../../interfaces/ticket/ReorderRequest';

interface IssuesBoardProps {
    tickets: Ticket[];
    sprints: Sprint[];
    onTicketReorder: (payload: ReorderRequest) => void;
    onTicketClick: (ticketId: string) => void;
}

const IssuesBoard: React.FC<IssuesBoardProps> = ({ tickets, sprints, onTicketReorder, onTicketClick }) => {
    const [sprintData, setSprintData] = useState<Map<string, Ticket[]>>(new Map());

    useEffect(() => {
        const newSprintData = new Map<string, Ticket[]>();
        sprints.forEach(sprint => newSprintData.set(sprint.id, []));
        newSprintData.set('backlog', []);

        tickets.forEach(ticket => {
            const sprintId = ticket.sprint?.id || 'backlog';
            const sprintTickets = newSprintData.get(sprintId) || [];
            sprintTickets.push(ticket);
            newSprintData.set(sprintId, sprintTickets);
        });

        newSprintData.forEach((sprintTickets) => {
            sprintTickets.sort((a, b) => a.sortOrder - b.sortOrder);
        });

        setSprintData(newSprintData);
    }, [tickets, sprints]);

    const onDragEnd = async (result: DropResult) => {
        const { source, destination, draggableId } = result;

        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }

        // --- Create the new state optimistically ---
        const newSprintData = new Map(sprintData);
        const sourceTickets = Array.from(newSprintData.get(source.droppableId) || []);
        const [movedTicket] = sourceTickets.splice(source.index, 1);
        newSprintData.set(source.droppableId, sourceTickets);

        const destTickets = Array.from(newSprintData.get(destination.droppableId) || []);
        destTickets.splice(destination.index, 0, movedTicket);
        newSprintData.set(destination.droppableId, destTickets);

        // Perform a local optimistic update for immediate visual feedback
        setSprintData(newSprintData);

        // --- Create the API payload ---
        const ticketId = draggableId;
        const payload: ReorderRequest = {
            ticketIds: [ticketId],
            targetSprintId: destination.droppableId === 'backlog' ? null : destination.droppableId,
        };
        
        const insertBeforeTicket = destTickets[destination.index + 1];
        if (insertBeforeTicket) {
            payload.insertBeforeTicketId = insertBeforeTicket.id;
        }

        // --- Call the parent handler to trigger the API call ---
        onTicketReorder(payload);
    };

    const currentSprint = sprints.find(s => !s.completed);
    console.log('Sprint Data Tickets', sprintData)

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <div className="issues-board-vertical">
                {currentSprint && (
                    <SprintLane
                        key={currentSprint.id}
                        sprintId={currentSprint.id}
                        sprintName={currentSprint.name}
                        tickets={sprintData.get(currentSprint.id) || []}
                        onTicketClick={onTicketClick}
                    />
                )}
                <SprintLane
                    key="backlog"
                    sprintId="backlog"
                    sprintName="Backlog"
                    tickets={sprintData.get('backlog') || []}
                    onTicketClick={onTicketClick}
                />
            </div>
        </DragDropContext>
    );
};

export default IssuesBoard;